import _extends from "@babel/runtime/helpers/extends";
import React, { createContext, useContext, useMemo } from 'react';
import deepEqual from 'deep-equal';
import memo from 'memoize-one';
import { JiraCurrentUserClient } from './jira-current-user-client';
import { JiraSearchClient } from './jira-search-client';
import { CollaborationGraphClient, extractSiteUrl } from '../../common/clients';
const ERROR_MESSAGE = 'Jira search client has not been instantiated. This is most likely because you are missing a JiraSearchClientProvider.';
const defaultCollaborationGraphConfig = {
  collaborationGraphUrl: 'gateway/api/collaboration',
  useCollaborationGraphForRecents: false
};
const getCollabGraphClientWithConfig = memo(config => new CollaborationGraphClient({
  collaborationGraphUrl: config.collaborationGraphUrl,
  cloudId: config.cloudId,
  isMultiSite: config.isMultiSite,
  sites: config.sites
}), deepEqual);
export const JiraSearchClientContext = /*#__PURE__*/createContext({});
const DEFAULT_SITES = [];
export const JiraSearchClientProvider = ({
  aggregatorUrl,
  cloudId,
  isUserAnonymous,
  collaborationGraphUrl,
  children,
  baseUrl,
  accountId,
  siteUrl = '',
  recentIssuesSupplier,
  currentUserSupplier,
  sites = DEFAULT_SITES
}) => {
  siteUrl = useMemo(() => extractSiteUrl({
    siteUrl,
    sites
  }), [siteUrl, sites]);
  const searchClient = useMemo(() => new JiraSearchClient({
    siteMasterList: sites,
    url: aggregatorUrl,
    cloudId,
    isUserAnonymous,
    recentIssuesSupplier,
    siteUrl
  }), [aggregatorUrl, cloudId, isUserAnonymous, recentIssuesSupplier, siteUrl, sites]);
  const collabGraphClient = getCollabGraphClientWithConfig({
    // If no collaborationGraphUrl is passed as a prop, use the fallback (default) URL
    collaborationGraphUrl: collaborationGraphUrl || defaultCollaborationGraphConfig.collaborationGraphUrl,
    cloudId: cloudId,
    isMultiSite: sites.length > 1,
    sites
  });
  const currentUserClient = useMemo(() => accountId ? new JiraCurrentUserClient({
    accountId,
    baseUrl,
    currentUserSupplier
  }) : undefined, [accountId, baseUrl, currentUserSupplier]);
  return /*#__PURE__*/React.createElement(JiraSearchClientContext.Provider, {
    value: {
      searchClient,
      collabGraphClient,
      currentUserClient,
      siteUrl,
      cloudId,
      sites
    }
  }, children);
};
export const withClients = Component => {
  return props => /*#__PURE__*/React.createElement(JiraSearchClientContext.Consumer, null, ({
    searchClient,
    collabGraphClient,
    currentUserClient,
    cloudId,
    sites
  }) => {
    if (!searchClient) {
      throw new Error(ERROR_MESSAGE);
    }
    const {
      ...rest
    } = props;
    return /*#__PURE__*/React.createElement(Component, _extends({}, rest, {
      searchClient: searchClient,
      collabGraphClient: collabGraphClient,
      currentUserClient: currentUserClient,
      cloudId: cloudId,
      sites: sites
    }));
  });
};
export function useJiraSearchClientContext() {
  const {
    searchClient,
    collabGraphClient,
    currentUserClient,
    siteUrl,
    sites,
    cloudId
  } = useContext(JiraSearchClientContext);
  if (!searchClient) {
    throw new Error(ERROR_MESSAGE);
  }
  return {
    searchClient,
    collabGraphClient,
    currentUserClient,
    siteUrl,
    cloudId,
    sites
  };
}